import * as React from 'react';
import { FaUserTie, FaPeopleGroup } from "react-icons/fa6";
import { HiMiniUserGroup } from "react-icons/hi2";
import { IoPencil, IoTrashOutline, IoSearchOutline, IoClose } from "react-icons/io5";
import { getAllTeams, logout, stringAvatar } from '../../../service/service-call';
import AddTeams from './teamAdd';
import TeamDelete from './teamDelete';
import EditTeams from './teamEdit';
import ViewTeams from './teamView';

function InitialsAvatar({ name, size = 36 }) {
  const { sx, children } = stringAvatar(name) || {};
  const bg = sx?.bgcolor || "#262673";
  return (
    <div
      title={name}
      style={{ width: size, height: size, backgroundColor: bg }}
      className="flex shrink-0 items-center justify-center rounded-full border-2 border-white text-[11px] font-bold text-white shadow-sm"
    >
      {children}
    </div>
  );
}

function AvatarStack({ members }) {
  if (!members || members.length === 0) return null;
  const visible = members.slice(0, 3);
  const remaining = members.slice(3);

  return (
    <div className="flex items-center">
      <div className="flex -space-x-2">
        {visible.map((m, i) => (
          <InitialsAvatar key={i} name={`${m.firstName} ${m.lastName}`} />
        ))}
      </div>
      {remaining.length > 0 && (
        <div
          title={remaining.map(m => `${m.firstName} ${m.lastName}`).join('\n')}
          className="-ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-[11px] font-bold text-gray-600 shadow-sm"
        >
          +{remaining.length}
        </div>
      )}
    </div>
  );
}

export default function Teams() {
  const [searchText, setSearchText] = React.useState("");
  const [isViewTeamOpen, setIsViewTeamOpen] = React.useState(false);
  const [isAddTeamOpen, setIsAddTeamOpen] = React.useState(false);
  const [isEditTeamOpen, setIsEditTeamOpen] = React.useState(false);
  const [isDeleteTeamOpen, setIsDeleteTeamOpen] = React.useState(false);
  const [members, setTeams] = React.useState([]);
  const [selectedTeams, setSelectedTeams] = React.useState({});
  const [selectedTeamId, setSelectedTeamId] = React.useState("");
  const [selectedTeamName, setSelectedTeamName] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    getAllTeamsByUser(searchText);
  }, []);

  function toggleAddTeamModal() {
    setIsAddTeamOpen(!isAddTeamOpen);
    if (isAddTeamOpen === true) {
      getAllTeamsByUser(searchText);
    }
  }

  function editTeam(id) {
    setSelectedTeamId(id);
    toggleEditTeamModal();
  }

  function deleteTeam(id, name) {
    setSelectedTeamId(id);
    setSelectedTeamName(name);
    toggleDeleteTeamModal();
  }

  function toggleEditTeamModal() {
    setIsEditTeamOpen(!isEditTeamOpen);
    if (isEditTeamOpen === true) {
      getAllTeamsByUser(searchText);
    }
  }

  function toggleDeleteTeamModal() {
    setIsDeleteTeamOpen(!isDeleteTeamOpen);
    if (isDeleteTeamOpen === true) {
      getAllTeamsByUser("");
    }
  }

  function toggleViewTeamModal(each) {
    setSelectedTeams(each);
    setIsViewTeamOpen(!isViewTeamOpen);
  }

  function getAllTeamsByUser(value) {
    setIsLoading(true);
    getAllTeams(value).then(resp => {
      setIsLoading(false);
      if (resp.status === 401) {
        logout();
      }
      resp.json().then(data => {
        setTeams(data);
      });
    }).catch(error => {
      console.log("login user err " + error);
    });
  }

  const role = localStorage.getItem("role");
  const userId = localStorage.getItem("userId");

  function canEditDelete(each) {
    if (role === "ROLE_SUPER_ADMIN") return { edit: true, delete: true };
    if (role === "ROLE_ADMIN" && userId === each.teamLeadId.id) return { edit: true, delete: false };
    return { edit: false, delete: false };
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <IoSearchOutline size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchText}
            placeholder="Search team here.."
            onChange={(e) => {
              setSearchText(e.target.value);
              getAllTeamsByUser(e.target.value);
            }}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 outline-none transition-colors focus:border-[#262673] focus:ring-2 focus:ring-[#262673]/10"
          />
        </div>

        {role !== "ROLE_USER" && (
          <button
            onClick={toggleAddTeamModal}
            title="Add Team"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#262673] text-white shadow-lg shadow-[#262673]/30 transition-all duration-300 hover:-translate-y-1 hover:scale-110 hover:bg-orange-500 hover:shadow-orange-500/30"
          >
            <FaPeopleGroup size={18} />
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#262673]" />
        </div>
      )}

      {/* Team grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {members && members.length > 0 ? (
            members.map((each, ind) => {
              const perms = canEditDelete(each);
              const isActive = each.status === "ACTIVE";
              return (
                <div
                  key={ind}
                  className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#262673]/10 hover:shadow-lg"
                >
                  {/* Status icon */}
                  <button onClick={() => toggleViewTeamModal(each)} className="shrink-0 pt-0.5">
                    <HiMiniUserGroup size={34} color={isActive ? "#16a34a" : "#ef4444"} />
                  </button>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <button onClick={() => toggleViewTeamModal(each)} className="block text-left">
                      <p className="truncate text-sm font-bold text-[#262673]">{each.teamName}</p>
                      <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-gray-400">
                        <FaUserTie size={11} />
                        {each.teamLeadId.firstName} {each.teamLeadId.lastName}
                      </p>
                    </button>

                    <div className="mt-3">
                      <AvatarStack members={each.members} />
                    </div>
                  </div>

                  {/* Actions */}
                  {(perms.edit || perms.delete) && (
                    <div className="flex shrink-0 flex-col gap-1 border-l border-gray-100 pl-2">
                      {perms.edit && (
                        <button
                          onClick={() => editTeam(each.id)}
                          title="Edit"
                          className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-green-50 hover:text-green-600"
                        >
                          <IoPencil size={14} />
                        </button>
                      )}
                      {perms.delete && (
                        <button
                          onClick={() => deleteTeam(each.id, each.teamName)}
                          title="Delete"
                          className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        >
                          <IoTrashOutline size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-200 py-16 text-gray-400">
              <FaPeopleGroup size={26} className="text-gray-300" />
              <p className="text-sm font-medium">No teams found</p>
            </div>
          )}
        </div>
      )}

      {/* Add team modal */}
      {isAddTeamOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-2xl">
            <AddTeams addTeamsModal={toggleAddTeamModal} />
          </div>
        </div>
      )}

      {/* Edit team modal */}
      {isEditTeamOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-2xl">
            <EditTeams editTeamModal={toggleEditTeamModal} selectedTeamId={selectedTeamId} />
          </div>
        </div>
      )}

      {/* View team modal — wider */}
      {isViewTeamOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-2xl">
            <ViewTeams viewTeamModal={toggleViewTeamModal} team={selectedTeams} />
          </div>
        </div>
      )}

      {/* Delete team modal */}
      {isDeleteTeamOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-2xl">
            <TeamDelete deleteTeamsModal={toggleDeleteTeamModal} teamId={selectedTeamId} name={selectedTeamName} />
          </div>
        </div>
      )}
    </div>
  );
}